import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import OccupancyItem from './Common/OccupancyItem'

import AddRoomButton from 'components/Common/AddRoomButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import config from 'constants/config'
import { maxFlightChildAge } from 'constants/flight'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import {
  isValidAccommodationSearchOccupancy,
  isValidFlightsOccupants,
} from 'lib/offer/occupancyUtils'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

interface Props {
  tripId: string
  onSetOccupancies: (occupancies: Array<App.Occupants>) => void
  initOccupants: Array<App.Occupants>
  isFlights?: boolean
}

const MAX_CHILD_AGE_ACCOMM_SEARCH = 17

const StyledModalContent = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  justify-content: end;
`

function SearchOccupancyPicker(props: Props) {
  const intl = useIntl()
  const { initOccupants, onSetOccupancies, isFlights = false } = props

  const maxChildAge = isFlights ?
    maxFlightChildAge :
    MAX_CHILD_AGE_ACCOMM_SEARCH

  const { closeModal, goBack } = useModalCallbacks()

  const { disableMultiRoom } = useContext(GlobalSearchStateContext)

  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(initOccupants)

  const isValidOccupany = useMemo(() => {
    if (isFlights) {
      return isValidFlightsOccupants(occupancies[0])
    }
    return isValidAccommodationSearchOccupancy(
      occupancies,
      MAX_CHILD_AGE_ACCOMM_SEARCH,
    )
  }, [occupancies, isFlights])

  const addRoom = useCallback((occupancies: Array<App.Occupants>) => {
    const newOccupancies = [
      ...occupancies,
      { ...config.search.defaultOccupants },
    ]
    setOccupancies(newOccupancies)
  }, [])

  const removeItem = useCallback(
    (occupancies: Array<App.Occupants>, index: number) => {
      const newOccupancies = [...occupancies]
      newOccupancies.splice(index, 1)
      setOccupancies(newOccupancies)
    },
    [],
  )

  const resetItem = useCallback((occupancies: Array<App.Occupants>, index: number) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...config.search.defaultOccupants }
    setOccupancies(newOccupancies)
  }, [])

  const changeItem = useCallback(
    (
      occupancies: Array<App.Occupants>,
      newOccupancy: App.Occupants,
      index: number,
    ) => {
      const newOccupancies = [...occupancies]
      newOccupancies[index] = newOccupancy
      setOccupancies(newOccupancies)
    },
    [],
  )

  const onApply = useCallback(
    (occupancies: Array<App.Occupants>) => {
      onSetOccupancies(occupancies)

      if (goBack) {
        goBack()
      }
    },
    [goBack, onSetOccupancies],
  )

  const selectTravellersTitle = intl.formatMessage({
    id: '61/Z9WlxmS',
    defaultMessage: 'Select your travellers',
    description: 'Modal title for traveller selection in flight occupancy picker',
  })

  const selectRoomsGuestsTitle = intl.formatMessage({
    id: 'f/C5LHKFMx',
    defaultMessage: 'Select your rooms & guests',
    description: 'Modal title for room and guest selection in accommodation occupancy picker',
  })

  const modalTitle = isFlights ? selectTravellersTitle : selectRoomsGuestsTitle

  const applyLabel = intl.formatMessage({
    id: '/y9Hyjyz+E',
    defaultMessage: 'Apply',
    description: 'Button text to apply occupancy selection in trip planner',
  })

  const roomTitle = (index: number) => intl.formatMessage(
    {
      id: 'BssIpNykwx',
      defaultMessage: 'Room {roomNumber}',
      description: 'Room number label in occupancy picker, e.g. Room 1, Room 2',
    },
    { roomNumber: index + 1 },
  )

  const onClickApply = useCallback(
    () => onApply(occupancies),
    [occupancies, onApply],
  )

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <StyledModalContent>
          <VerticalSpacer gap={8}>
            {occupancies.map((o, index) => (
              <OccupancyItem
                occupancy={o}
                itemId={index}
                key={index}
                title={isFlights ? undefined : roomTitle(index)}
                showRemove={index > 0}
                onReset={() => resetItem(occupancies, index)}
                onRemove={() => removeItem(occupancies, index)}
                onChange={(_id, occ) => {
                  changeItem(occupancies, occ, index)
                }}
                maxChildAge={maxChildAge}
              />
            ))}
            {!disableMultiRoom &&
              !isFlights &&
              config.accommodation.allowsMultiRoom && (
              <AddRoomButton
                roomsCount={occupancies.length}
                onClick={() => addRoom(occupancies)}
              />
            )}
          </VerticalSpacer>
        </StyledModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: applyLabel,
          onClick: onClickApply,
          disabled: !isValidOccupany,
        }}
      />
    </>
  )
}

export default SearchOccupancyPicker
